"use client"

import { useEffect } from "react"
import { QueryClient, useQuery } from "@tanstack/react-query"

import {
  getAuthentication,
  type UseGetAuthenticationParams,
} from "./get-authentication"

export {
  getAuthentication,
  oauthAvatarUrlFromUser,
  type UseGetAuthenticationParams,
} from "./get-authentication"

/**
 * Platform-agnostic auth query hook.
 * Accepts an optional `redirect` callback so routing stays in the app wrapper
 * (Next.js / Expo Router) rather than in this shared package.
 */
export default function useGetAuthenticationCore(
  {
    redirectIfNotAuthenticated = true,
    redirectTo = "/auth?sign=signin",
    requireEditor = false,
    redirectIfNotEditor = "/",
  }: UseGetAuthenticationParams = {},
  queryClient: QueryClient | undefined,
  redirect: ((to: string) => void) | undefined,
) {
  const result = useQuery(
    {
      queryFn: getAuthentication,
      queryKey: ["authentication"],
      retry: false,
      staleTime: 30_000,
    },
    queryClient,
  )
  const { data: authentication, isError, isLoading, isSuccess } = result

  useEffect(() => {
    if (isLoading) return
    if (isError || (!authentication?.isAuthenticated && redirectIfNotAuthenticated && redirectTo)) {
      redirect?.(redirectTo)
      return
    }
    if (requireEditor && authentication?.isAuthenticated && !authentication?.isEditor) {
      redirect?.(redirectIfNotEditor)
    }
  }, [
    authentication,
    isError,
    isLoading,
    isSuccess,
    redirect,
    redirectIfNotAuthenticated,
    redirectIfNotEditor,
    redirectTo,
    requireEditor,
  ])

  return result
}
